import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useMemo,
} from 'react';

import { initSocket, getSocket, connectSocket } from '../socket';
import { useAuthContext } from './AuthContext';
import { findOrCreateDM } from '../api/chatService';
import { authFetch } from '../Utilities/Api'; // ✅ Added for notification support

const ChatContext = createContext(null);

export const ChatProvider = ({ children }) => {
    const { user } = useAuthContext();

    // ✅ Ensures userId is a consistent string
    const resolvedUserId = useMemo(() =>
        user?._id ? String(user._id) : null,
        [user?._id]);

    const [activeChat, setActiveChat] = useState(() => {
        const saved = localStorage.getItem('activeChat');
        try { return saved ? JSON.parse(saved) : null; } catch (e) { return null; }
    });

    const [isChatOpen, setIsChatOpen] = useState(() => {
        return localStorage.getItem('isChatOpen') === 'true';
    });

    const [pinnedIds, setPinnedIds] = useState(() => {
        const saved = localStorage.getItem('pinnedChatIds');
        try { return saved ? JSON.parse(saved) : []; } catch (e) { return []; }
    });

    const [drafts, setDrafts] = useState(() => {
        const saved = localStorage.getItem('chatDrafts');
        try { return saved ? JSON.parse(saved) : {}; } catch (e) { return {}; }
    });

    const [isConnected, setIsConnected] = useState(false);
    const [isCreatingChat, setIsCreatingChat] = useState(false);

    // ---------------------------------------------------------
    // 💾 1. Sync State to Storage
    // ---------------------------------------------------------
    useEffect(() => {
        if (resolvedUserId) {
            localStorage.setItem('activeChat', JSON.stringify(activeChat));
            localStorage.setItem('isChatOpen', isChatOpen);
            localStorage.setItem('pinnedChatIds', JSON.stringify(pinnedIds));
            localStorage.setItem('chatDrafts', JSON.stringify(drafts));
        }
    }, [activeChat, isChatOpen, pinnedIds, drafts, resolvedUserId]);

    // ---------------------------------------------------------
    // 🧹 2. Cleanup on Logout
    // ---------------------------------------------------------
    useEffect(() => {
        if (!resolvedUserId) {
            console.log("🧹 [ChatContext] User logged out. Clearing chat state.");
            localStorage.removeItem('activeChat');
            localStorage.removeItem('chatDrafts');
            localStorage.removeItem('isChatOpen');
            localStorage.removeItem('pinnedChatIds');

            setActiveChat(null);
            setDrafts({});
            setIsChatOpen(false);
            setPinnedIds([]);
        }
    }, [resolvedUserId]);

    // ---------------------------------------------------------
    // 🚀 3. Core Actions
    // ---------------------------------------------------------
    const updateDraft = useCallback((chatId, text) => {
        setDrafts(prev => ({ ...prev, [chatId]: text }));
    }, []);

    const togglePin = useCallback((id) => {
        setPinnedIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    }, []);

    /**
     * ✅ SMART openChat
     * Works with full chat objects OR a simple chatId string (from notifications).
     */
    const openChat = useCallback(async (chatOrId) => {
        if (!chatOrId) {
            console.warn("⚠️ [ChatContext] openChat called with NULL!");
            setActiveChat(null);
            return;
        }

        // Case A: Received a String ID (e.g., from clicking a Notification)
        if (typeof chatOrId === 'string') {
            console.log("📡 [ChatContext] Fetching full chat for ID:", chatOrId);
            try {
                const res = await authFetch(`/chats/${chatOrId}`);
                if (!res.ok) throw new Error("Could not find chat");
                const fullChat = await res.json();

                setActiveChat(fullChat);
                setIsChatOpen(true);

                // Ensure socket joins this specific room
                const socket = getSocket();
                if (socket?.connected) {
                    socket.emit('join', chatOrId);
                }
            } catch (err) {
                console.error("❌ [ChatContext] Failed to open chat by ID:", err);
            }
            return;
        }

        // Case B: Received a full Chat Object
        console.log("🔓 [ChatContext] Opening chat object:", chatOrId?._id);
        setActiveChat(chatOrId);
        setIsChatOpen(true);
    }, []);

    const closeChat = useCallback(() => {
        console.log("🔒 [ChatContext] Closing chat window.");
        setIsChatOpen(false);
    }, []);

    const openChatWithFriend = useCallback(async (friendId) => {
        if (!resolvedUserId || isCreatingChat) return;

        try {
            setIsCreatingChat(true);
            const chat = await findOrCreateDM(friendId);
            const chatId = chat?._id || chat?.id;

            if (chat && chatId) {
                openChat(chat);
                const socket = getSocket();
                if (socket?.connected) {
                    socket.emit('join', String(chatId));
                }
            }
        } catch (err) {
            console.error("❌ [ChatContext] Failed to open DM:", err);
        } finally {
            setIsCreatingChat(false);
        }
    }, [resolvedUserId, openChat, isCreatingChat]);

    // ---------------------------------------------------------
    // 🔌 4. Socket Lifecycle Management
    // ---------------------------------------------------------
    useEffect(() => {
        if (!resolvedUserId) return;

        const socket = initSocket();
        connectSocket();

        const onConnect = () => {
            setIsConnected(true);
            socket.emit('join', resolvedUserId);
            console.log("📡 [ChatContext] Joined personal room:", resolvedUserId);

            if (activeChat?._id) {
                socket.emit('join', String(activeChat._id));
            }
        };

        const onDisconnect = () => setIsConnected(false);

        if (socket.connected) onConnect();

        socket.on('connect', onConnect);
        socket.on('disconnect', onDisconnect);

        return () => {
            socket.off('connect', onConnect);
            socket.off('disconnect', onDisconnect);
        };
    }, [resolvedUserId, activeChat?._id]);

    // ---------------------------------------------------------
    // 🎁 5. Provider Value
    // ---------------------------------------------------------
    const value = useMemo(() => ({
        activeChat,
        isChatOpen,
        setIsChatOpen,
        isConnected,
        isCreatingChat,
        openChat,
        closeChat,
        openChatWithFriend,
        pinnedIds,
        togglePin,
        drafts,
        updateDraft,
        socket: resolvedUserId ? getSocket() : null,
        currentUserId: resolvedUserId
    }), [
        activeChat, isChatOpen, isConnected, isCreatingChat,
        openChat, closeChat, openChatWithFriend, pinnedIds,
        togglePin, drafts, updateDraft, resolvedUserId
    ]);

    return (
        <ChatContext.Provider value={value}>
            {children}
        </ChatContext.Provider>
    );
};

export const useChatContext = () => {
    const ctx = useContext(ChatContext);
    if (!ctx) throw new Error('useChatContext must be used within ChatProvider');
    return ctx;
};

export default ChatContext;